import React, { useState } from 'react';
import {
  Typography,
  Box,
  Button,
  TextField,
  Paper,
  Container,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

export default function EditSubmissionPage() {
  const navigate = useNavigate();
  const [uniqueId, setUniqueId] = useState('');
  const [editCode, setEditCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [submission, setSubmission] = useState<any>(null);

  const validateSubmission = async () => {
    if (!uniqueId || !editCode) {
      setResult('Please provide both Unique ID and Edit Code');
      return;
    }

    setLoading(true);
    setResult('');
    setSubmission(null);

    try {
      console.log('Validating submission:', { uniqueId, editCode });

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/submissions/edit/${uniqueId}/${editCode}`
      );

      setSubmission(response.data);
      setResult(`✅ Submission found! Form: ${response.data.form?.title}`);

    } catch (error: any) {
      console.error('Validation failed:', error);
      setResult(`❌ Validation failed: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const proceedToEdit = () => {
    if (submission) {
      navigate(`/edit-submission/${uniqueId}/${editCode}`);
    }
  };

  const viewSubmission = () => {
    if (submission) {
      navigate(`/view-submission/${uniqueId}`);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <EditIcon sx={{ mr: 2 }} />
          Edit Your Submission
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
          Enter your submission details to edit your form response. You need both the Unique ID and Edit Code that were provided when you submitted the form.
        </Typography>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Submission Credentials
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Submission Unique ID"
                value={uniqueId}
                onChange={(e) => setUniqueId(e.target.value.toUpperCase())}
                margin="normal"
                placeholder="e.g., 1A2B3C4D5E6F7G8H9I0J1K2L3M4N5O6P"
                helperText="32-character unique identifier for your submission"
                inputProps={{ style: { fontFamily: 'monospace' } }}
              />
              
              <TextField
                fullWidth
                label="6-Digit Edit Code"
                value={editCode}
                onChange={(e) => setEditCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                margin="normal"
                placeholder="e.g., 123456"
                helperText="6-digit numeric code provided after submission"
                inputProps={{ 
                  style: { fontFamily: 'monospace' },
                  maxLength: 6
                }}
              />
            </Box>

            <Button
              variant="contained"
              onClick={validateSubmission}
              disabled={loading || !uniqueId || !editCode}
              startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
              size="large"
              fullWidth
            >
              {loading ? 'Validating...' : 'Validate Submission'}
            </Button>
          </CardContent>
        </Card>

        {result && (
          <Alert 
            severity={result.startsWith('✅') ? 'success' : 'error'}
            sx={{ mb: 3 }}
          >
            {result}
          </Alert>
        )}

        {submission && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Submission Details
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Form:</strong> {submission.form?.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Status:</strong> {submission.status}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Last Updated:</strong> {new Date(submission.updatedAt).toLocaleString()}
                </Typography>
                {submission.editHistory && submission.editHistory.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Edit History:</strong> {submission.editHistory.length} edit(s) made
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={proceedToEdit}
                  startIcon={<EditIcon />}
                  size="large"
                >
                  Edit Submission
                </Button>

                <Button
                  variant="outlined"
                  onClick={viewSubmission}
                  startIcon={<ViewIcon />}
                  size="large"
                >
                  View Only
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        <Alert severity="info">
          <Typography variant="body2" gutterBottom>
            <strong>How to find your credentials:</strong>
          </Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
            <li>Check your email confirmation (if provided during submission)</li>
            <li>Look at the success page after form submission</li>
            <li>Check any saved bookmarks or screenshots</li>
            <li>Contact the form administrator if you can't locate these details</li>
          </Typography>
        </Alert>
      </Paper>
    </Container>
  );
}
